import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "./utils";
import { useAuth } from "@/lib/AuthContext";
import { CreateProfileModal } from "@/components/shared/CreateProfileModal";
import { NotificationBell } from "@/components/shared/NotificationBell";
import { ProfileSelector } from "@/components/shared/ProfileSelector";
import { GlobalProvider } from "@/components/contexts/GlobalContext";
import { 
        LayoutDashboard, 
        BarChart3, 
        MessageSquare, 
        Brain, 
        AlertTriangle, 
        Plug, 
        Users, 
        Settings, 
        ChevronLeft,
        ChevronRight,
        LogOut,
        Building2,
        ChevronDown,
        Menu,
        X,
        Target,
        Shield,
        Wallet,
        Coins,
        Headphones,
        Lightbulb,
        BookOpen
      } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import TrialBanner from "@/components/TrialBanner";
import ReconnectBanner from "@/components/ReconnectBanner";
import {
  ChatNavSlotsProvider,
  useChatNavSlots,
  requestNewChat,
} from "@/components/chat/ChatNavSlotsContext";
import { ChatNavNewChatButton } from "@/components/chat/ChatSidebar";

/** DOM mount point — ChatAssistant portals Recents here (state stays in ChatAssistant). */
function ChatNavRecentsSlot() {
  const { setRecentsSlot, navCollapsed } = useChatNavSlots();
  return (
    <div
      ref={setRecentsSlot}
      className={navCollapsed ? "hidden" : undefined}
      data-chat-nav-recents-slot
    />
  );
}

function NavItemLink({ item, currentPageName, collapsed, onNavigate }) {
  const isActive = currentPageName === item.page;
  const Icon = item.icon;
  return (
    <Link
      to={createPageUrl(item.page)}
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-3 rounded-md text-xs font-medium transition-colors duration-150",
        isActive
          ? "bg-ad-primary/10 text-ad-primary border-l-2 border-ad-primary pl-[10px] pr-3 py-2"
          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 px-3 py-2",
      )}
    >
      <Icon className="w-4 h-4 shrink-0 text-current" />
      {!collapsed && (
        <span className="flex items-center gap-2">
          {item.name}
          {item.badge && (
            <span className="bg-[#dbeafe] text-ad-primary text-[8px] font-bold rounded px-1.5 py-0.5 uppercase">
              {item.badge}
            </span>
          )}
        </span>
      )}
    </Link>
  );
}

const navSections = [
  {
    title: "ANALYTICS",
    items: [
      { name: "Dashboard",      icon: LayoutDashboard, page: "Dashboard" },
      { name: "Insights",       icon: Lightbulb,       page: "Insights" },
      { name: "Reports",        icon: BarChart3,        page: "Reports" },
      { name: "Target & Pacing", icon: Target,        page: "BudgetTargets" },
    ]
  },
  {
    title: "AI FEATURES",
    items: [
      { name: "Chat Assistant",   icon: MessageSquare, page: "ChatAssistant" },
      { name: "Briefs",           icon: BookOpen,      page: "Briefs" },
      { name: "Knowledge Center", icon: Brain,         page: "KnowledgeCenter", badge: "Profiles" },
      { name: "Audits",           icon: AlertTriangle, page: "Audits" },
    ]
  },
  {
    title: "MANAGEMENT",
    items: [
      { name: "Connected Accounts",  icon: Plug,   page: "Integrations" },
      { name: "Team",                icon: Users,  page: "Team" },
    ]
  },
  {
    title: "SETTINGS",
    items: [
      { name: "Billing",        icon: Wallet,     page: "Billing" },
      { name: "Settings",       icon: Settings,   page: "Settings" },
    ]
  }
];

export default function Layout({ children, currentPageName }) {
  const isChatPage = currentPageName === "ChatAssistant";
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user: authUser, logout } = useAuth();
  const { isSaasOwner, isPlatformAdmin, isSupportStaff } = usePlatformPermissions();
  const navigate = useNavigate();

  const [upgradeModal, setUpgradeModal] = useState(null); // null | { credits_required, credits_available, upgrade_prompt }

  const handleNewChatNav = useCallback(() => {
    setMobileOpen(false);
    navigate(createPageUrl("ChatAssistant"));
    // Defer so ChatAssistant can mount / attach its startNewChat listener.
    requestAnimationFrame(() => requestNewChat());
  }, [navigate]);

  // Global credit events — insufficient-credits modal only (balance refresh lives in CreditsCard)
  useEffect(() => {
    const onInsufficient = (e) => setUpgradeModal(e.detail || {});
    window.addEventListener("credits:insufficient", onInsufficient);
    return () => {
      window.removeEventListener("credits:insufficient", onInsufficient);
    };
  }, []);

  // Build platform nav section dynamically based on roles
  const platformItems = [];
  if (isSaasOwner || isPlatformAdmin) {
    platformItems.push({ name: "Super Admin", icon: Shield, page: "SuperAdmin" });
  }
  if (isSupportStaff || isSaasOwner || isPlatformAdmin) {
    platformItems.push({ name: "Support Portal", icon: Headphones, page: "Support" });
  }

  const allNavSections = platformItems.length > 0
    ? [...navSections, { title: "PLATFORM", items: platformItems }]
    : navSections;

  // Derive display values from auth context
  const user = authUser ? {
    full_name: authUser.email?.split('@')[0] || 'User',
    email: authUser.email || '',
  } : null;

  const handleLogout = () => {
    logout(true);
  };

  const brandBlock = (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 bg-ad-primary rounded-lg flex items-center justify-center flex-shrink-0">
        <Brain className="w-5 h-5 text-white" />
      </div>
      <span className="font-bold text-sm text-gray-900 tracking-tight">Rimora</span>
    </div>
  );

  return (
    <GlobalProvider>
    <ChatNavSlotsProvider navCollapsed={collapsed}>
      <div className="min-h-screen bg-ad-bg text-gray-900 font-sans">
      <style>{`
        :root {
          --gradient-primary: linear-gradient(135deg, #6366f1 0%, #3388FF 100%);
          --gradient-accent: linear-gradient(135deg, #4f46e5 0%, #1A6FE8 100%);
          --glow-primary: 0 0 20px rgba(99, 102, 241, 0.3);
          --glow-cyan: 0 0 20px rgba(51, 136, 255, 0.3);
        }
        
        .gradient-text {
          background: var(--gradient-primary);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .glow-button {
          background: var(--gradient-primary);
          box-shadow: var(--glow-primary);
          transition: all 0.3s ease;
        }
        
        .glow-button:hover {
          box-shadow: 0 0 30px rgba(99, 102, 241, 0.5);
          transform: translateY(-1px);
        }
        
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>

      {/* Mobile Header — chat: empty centre (composer owns scope); other pages keep brand */}
      <header className="lg:hidden fixed top-0 left-0 right-0 min-h-[72px] bg-white border-b border-ad-border z-50 flex items-center gap-2 px-3">
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-gray-400 shrink-0"
          aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        <div className="min-w-0 flex-1 overflow-hidden">
          {isChatPage ? null : brandBlock}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <ProfileSelector className="bg-white hover:bg-gray-50 border border-transparent hover:border-ad-border rounded-lg transition-colors text-gray-900 max-w-[140px] [&_svg]:text-gray-400" />
          <div className="inline-flex rounded-md p-1 text-gray-400 [&_button]:rounded-md [&_button]:p-1 [&_button]:text-gray-400 [&_button]:hover:text-gray-600 [&_button]:hover:bg-transparent">
            <NotificationBell />
          </div>
        </div>
      </header>

      {/* Desktop Header — chat: right-side profile + bell only; other pages keep brand left */}
      <header
        className={cn(
          "hidden lg:flex fixed top-0 left-0 right-0 min-h-[72px] bg-white border-b border-ad-border z-40 items-center px-6",
          isChatPage ? "justify-end" : "justify-between",
        )}
        style={{ marginLeft: collapsed ? '56px' : '220px' }}
      >
        {!isChatPage && brandBlock}

        <div className="flex items-center gap-3">
          <ProfileSelector className="bg-white hover:bg-gray-50 border border-transparent hover:border-ad-border rounded-lg transition-colors text-gray-900 [&_svg]:text-gray-400" />
          <div className="inline-flex rounded-md p-1 text-gray-400 [&_button]:rounded-md [&_button]:p-1 [&_button]:text-gray-400 [&_button]:hover:text-gray-600 [&_button]:hover:bg-transparent">
            <NotificationBell />
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 h-full bg-white border-r border-ad-border z-50 transition-all duration-300",
        "motion-reduce:transition-none",
        collapsed ? "w-[56px]" : "w-[220px]",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center gap-3 px-4 border-b border-ad-border">
            <div className="w-8 h-8 bg-ad-primary rounded-lg flex items-center justify-center flex-shrink-0">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <span className={cn("font-bold text-sm text-gray-900 tracking-tight", collapsed && "hidden")}>
              Rimora
            </span>
          </div>

          {/* Navigation — New chat at top; Recents capped under Chat Assistant; items below remain reachable */}
          <nav className="flex-1 py-3 flex flex-col min-h-0 overflow-hidden">
            <div className="px-1 shrink-0 mb-2">
              <ChatNavNewChatButton collapsed={collapsed} onNavigateToChat={handleNewChatNav} />
            </div>
            {(() => {
              const closeMobile = () => setMobileOpen(false);
              // Split so Recents cannot push KC / Settings off by growing unbounded.
              const upper = [];
              const lower = [];
              let pastChat = false;
              for (const section of allNavSections) {
                const before = [];
                const after = [];
                for (const item of section.items) {
                  if (!pastChat) {
                    before.push(item);
                    if (item.page === "ChatAssistant") pastChat = true;
                  } else {
                    after.push(item);
                  }
                }
                if (before.length) upper.push({ title: section.title, items: before, showRecents: before.some((i) => i.page === "ChatAssistant") });
                if (after.length) lower.push({ title: section.title, items: after, showHeading: before.length === 0 });
              }
              return (
                <>
                  <div className="shrink-0">
                    {upper.map((section) => (
                      <div key={`upper-${section.title}`}>
                        {!collapsed && (
                          <div className="px-2 mb-1">
                            <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
                              {section.title}
                            </p>
                          </div>
                        )}
                        {section.items.map((item) => (
                          <React.Fragment key={item.page}>
                            <NavItemLink
                              item={item}
                              currentPageName={currentPageName}
                              collapsed={collapsed}
                              onNavigate={closeMobile}
                            />
                            {item.page === "ChatAssistant" && <ChatNavRecentsSlot />}
                          </React.Fragment>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto mt-2">
                    {lower.map((section, idx) => (
                      <div key={`lower-${section.title}`} className={idx > 0 ? "mt-6" : ""}>
                        {!collapsed && (section.showHeading || section.title) && (
                          <div className="px-2 mb-1">
                            <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
                              {section.title}
                            </p>
                          </div>
                        )}
                        {section.items.map((item) => (
                          <NavItemLink
                            key={item.page}
                            item={item}
                            currentPageName={currentPageName}
                            collapsed={collapsed}
                            onNavigate={closeMobile}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </nav>

          {!collapsed && <TrialBanner />}

          {/* User area */}
          {user && (
            <div
              className={cn(
                "hidden lg:flex border-t border-ad-border px-3 py-3",
                collapsed ? "flex-col items-center justify-center gap-2" : "flex-row items-center gap-2"
              )}
            >
              <div className="w-8 h-8 rounded-full bg-ad-primary flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                {user?.full_name?.charAt(0) || "U"}
              </div>
              {!collapsed && (
                <>
                  <span className="text-xs font-medium text-gray-700 truncate flex-1 min-w-0">
                    {user?.full_name || "User"}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="text-gray-400 hover:text-gray-600 shrink-0"
                        aria-label="Account menu"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white border border-ad-border text-gray-900 shadow-dropdown" align="end">
                      <DropdownMenuItem asChild className="cursor-pointer hover:bg-gray-50">
                        <Link to={createPageUrl("Settings")} onClick={() => setMobileOpen(false)}>
                          <Settings className="w-4 h-4 mr-2 text-gray-400" /> Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleLogout} className="cursor-pointer hover:bg-gray-50 text-red-600">
                        <LogOut className="w-4 h-4 mr-2" /> Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>
          )}

          {/* Collapse Button */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex items-center justify-center h-12 border-t border-ad-border text-gray-600 hover:text-gray-900 transition-colors"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main Content */}
      <main
        className={cn("min-h-screen transition-all duration-300 pt-[72px] lg:pt-[72px] bg-ad-bg",
          collapsed ? "lg:pl-[56px]" : "lg:pl-[220px]"
        )}
      >
        <ReconnectBanner />
        <div className={isChatPage ? "w-full" : "w-full px-6 py-6"}>
          {children}
        </div>
      </main>

      {/* Global Modals */}
      <CreateProfileModal open={false} onOpenChange={() => {}} />

      {/* Upgrade modal — shown on 402 from any endpoint */}
      {upgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white border border-ad-border w-full max-w-sm mx-4 rounded-xl border border-red-500/20 p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
                <Coins className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-ad-text">Insufficient Credits</h3>
                <p className="text-xs text-ad-label mt-0.5">
                  {upgradeModal.credits_required != null
                    ? `Need ${upgradeModal.credits_required} credits — you have ${upgradeModal.credits_available ?? 0}`
                    : "You've run out of credits"}
                </p>
              </div>
            </div>
            <p className="text-sm text-ad-label mb-5">
              {upgradeModal.upgrade_prompt || "Purchase additional credits or upgrade your plan to continue."}
            </p>
            <div className="flex gap-3">
              <Button
                className="flex-1 glow-button text-white text-sm"
                onClick={() => { setUpgradeModal(null); navigate(`${createPageUrl("Billing")}?tab=credits`); }}
              >
                Buy Credits
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-ad-border text-gray-600 hover:bg-white/5 text-sm"
                onClick={() => setUpgradeModal(null)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ChatNavSlotsProvider>
    </GlobalProvider>
  );
}
